import { cn } from '@/lib/utils';

/** Dense KPI tile — neutral surface, minimal motion (product UI, not marketing landing). */
export default function DashboardKpiCard({
  title,
  value,
  subtitle,
  icon: Icon,
  tone = 'default',
  compact = false,
  className,
  onClick,
}) {
  const borderAccent = {
    default: 'border-border hover:border-border/90',
    primary: 'border-border hover:border-primary/30',
    success: 'border-border hover:border-success/25',
    warning: 'border-border hover:border-warning/30',
    destructive: 'border-border hover:border-destructive/25',
  };

  const iconStyle = {
    default: 'text-muted-foreground bg-muted/60',
    primary: 'text-primary bg-primary/10',
    success: 'text-success bg-success/10',
    warning: 'text-warning bg-warning/10',
    destructive: 'text-destructive bg-destructive/10',
  };

  const Comp = onClick ? 'button' : 'div';

  if (compact) {
    return (
      <Comp
        type={onClick ? 'button' : undefined}
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border bg-card px-2.5 py-2 text-left shadow-none transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          onClick && 'cursor-pointer hover:bg-muted/30',
          borderAccent[tone],
          className
        )}
      >
        {Icon && (
          <span
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded [&_svg]:h-3 [&_svg]:w-3',
              iconStyle[tone]
            )}
          >
            <Icon aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] font-medium uppercase tracking-wide text-foreground/65">{title}</p>
          <p className="font-mono text-base font-semibold leading-tight tracking-tight text-foreground tabular-nums">
            {value}
          </p>
        </div>
      </Comp>
    );
  }

  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex w-full flex-col rounded-md border bg-card p-3 text-left shadow-none transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        onClick && 'cursor-pointer hover:bg-muted/30',
        borderAccent[tone],
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-foreground/65">{title}</p>
        {Icon && (
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md [&_svg]:h-[14px] [&_svg]:w-[14px]',
              iconStyle[tone]
            )}
          >
            <Icon aria-hidden />
          </span>
        )}
      </div>
      <p className="mt-2 font-mono text-xl font-semibold tracking-tight text-foreground tabular-nums">
        {value}
      </p>
      {subtitle && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>}
    </Comp>
  );
}
